import { useState } from "react";
import { Link2, Plus, Trash2, Pause, Play } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { IDS } from "../constants/testIds";

export default function PatchDialog({ patches, channels, onAdd, onToggle, onDelete }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState([]);
  const byId = Object.fromEntries(channels.map(c => [c.id, c]));
  const live = patches.filter(p => p.active).length;

  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const submit = async () => {
    if (!name.trim() || picked.length < 2) return;
    await onAdd({ name: name.trim(), channel_ids: picked });
    setName(""); setPicked([]); setCreating(false);
  };
  const showForm = creating || patches.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreating(false); }}>
      <DialogTrigger asChild>
        <Button data-testid={IDS.patches.button} size="sm" variant={live ? "default" : "outline"} className={`gap-1.5 ${live ? "bg-amber-500 hover:bg-amber-600 text-slate-900" : ""}`} disabled={channels.length < 2} title="Tie channels together">
          <Link2 className="h-4 w-4" /> Patch{live > 0 && <span className="font-mono text-[10px] px-1 rounded bg-slate-900/15">{live}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={IDS.patches.dialog} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Channel patches</DialogTitle>
          <DialogDescription>Tie two or more channels so field units on different channels can hear each other.</DialogDescription>
        </DialogHeader>

        {patches.length > 0 && (
          <div data-testid={IDS.patches.root} className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto thin-scroll">
            {patches.map(p => (
              <div key={p.id} data-testid={IDS.patches.item(p.id)} className={`px-3 py-2.5 flex items-center gap-3 ${p.active ? "" : "opacity-60"}`}>
                <div className={`h-8 w-8 rounded-md grid place-items-center flex-shrink-0 ${p.active ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-400"}`}><Link2 className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {p.name}
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "LIVE" : "PAUSED"}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.channel_ids.map(id => byId[id]).filter(Boolean).map(ch => (
                      <span key={ch.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50"><span className="h-1.5 w-1.5 rounded-full" style={{ background: ch.color }} />{ch.name}</span>
                    ))}
                  </div>
                </div>
                <button data-testid={IDS.patches.toggle(p.id)} onClick={() => onToggle(p.id)} className="text-slate-500 hover:text-slate-900" aria-label={p.active ? "Pause patch" : "Resume patch"}>{p.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
                <button data-testid={IDS.patches.delete(p.id)} onClick={() => onDelete(p.id)} className="text-slate-400 hover:text-red-600" aria-label="Delete patch"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}

        {showForm ? (
          <div className="grid gap-3 py-1">
            <div className="grid gap-1.5">
              <Label>Patch name</Label>
              <Input data-testid={IDS.patches.name} value={name} onChange={e => setName(e.target.value)} placeholder="Incident 42 — Ops + Security" />
            </div>
            <div className="grid gap-1.5">
              <Label>Channels ({picked.length} selected, min 2)</Label>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-48 overflow-y-auto thin-scroll">
                {channels.map(ch => (
                  <label key={ch.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <Checkbox data-testid={IDS.patches.channelOption(ch.id)} checked={picked.includes(ch.id)} onCheckedChange={() => togglePick(ch.id)} />
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: ch.color }} />
                    <span className="truncate">{ch.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              {patches.length > 0 && <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>}
              <Button data-testid={IDS.patches.submit} onClick={submit} disabled={!name.trim() || picked.length < 2}>Create patch</Button>
            </DialogFooter>
          </div>
        ) : (
          <DialogFooter>
            <Button data-testid={IDS.patches.add} variant="outline" className="gap-1.5" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New patch</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
